import React, { useRef, useState } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import styled from 'styled-components';

import { FractionalStar } from 'components/core/fractional-star';

const MAX_RATING = 5;
const STEP = 0.5;

const clampRating = (value: number): number => Math.min(MAX_RATING, Math.max(STEP, Math.round(value / STEP) * STEP));

const ratingLabel = (value: number): string => `${value} ${value === 1 ? 'star' : 'stars'}`;

/** Map an option value (0.5–5 in 0.5 steps) to its 0-based position in the radio list. */
const optionIndex = (value: number): number => Math.round(value / STEP) - 1;

type StarRatingInputProps = {
  /** react-hook-form field name; value is a number 0.5-5 in 0.5 steps (0 = unrated) */
  name: string;
  size?: number;
  disabled?: boolean;
};

/**
 * Required 0.5-5 star rating input (half-star steps). Integrates with
 * react-hook-form via `Controller`. Each star is split into a left (x.5) and
 * right (x.0) click target, with a mouse hover preview.
 *
 * Keyboard support follows the WAI-ARIA radio group pattern: the group is a
 * single tab stop (roving tabindex), arrow keys move between and select
 * options (wrapping at the ends), Home/End jump to the first/last option, and
 * Space/Enter selects the focused option.
 */
export const StarRatingInput = ({ name, size = 28, disabled = false }: StarRatingInputProps): JSX.Element => {
  const { control } = useFormContext();
  const [hovered, setHovered] = useState(0);
  const optionRefs = useRef<(HTMLSpanElement | null)[]>([]);

  const focusOption = (value: number): void => {
    optionRefs.current[optionIndex(value)]?.focus();
  };

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={0}
      render={({ value, onChange }): JSX.Element => {
        const rating: number = value ?? 0;
        const displayed = hovered || rating;
        // The single tab stop: the selected option, or the first option when unrated.
        const focusableValue = rating || STEP;

        const setRating = (next: number): void => {
          if (!disabled) {
            onChange(clampRating(next));
          }
        };

        const handleKeyDown = (event: React.KeyboardEvent<HTMLSpanElement>, optionValue: number): void => {
          if (disabled) {
            return;
          }

          let next: number;
          switch (event.key) {
            case 'ArrowRight':
            case 'ArrowUp':
              next = optionValue >= MAX_RATING ? STEP : optionValue + STEP;
              break;
            case 'ArrowLeft':
            case 'ArrowDown':
              next = optionValue <= STEP ? MAX_RATING : optionValue - STEP;
              break;
            case 'Home':
              next = STEP;
              break;
            case 'End':
              next = MAX_RATING;
              break;
            case ' ':
            case 'Enter':
              next = optionValue;
              break;
            default:
              return;
          }

          event.preventDefault();
          setRating(next);
          focusOption(next);
        };

        return (
          <StarRow role='radiogroup' aria-label='Rate this item' aria-required onMouseLeave={() => setHovered(0)}>
            {Array.from({ length: MAX_RATING }, (_, index) => {
              const starValue = index + 1;
              const halfValue = starValue - STEP;
              return (
                <FractionalStar key={starValue} fillPercent={(displayed - index) * 100} size={size}>
                  {[halfValue, starValue].map((optionValue, half) => (
                    <HalfTarget
                      key={optionValue}
                      ref={(el: HTMLSpanElement | null) => {
                        optionRefs.current[optionIndex(optionValue)] = el;
                      }}
                      $side={half === 0 ? 'left' : 'right'}
                      role='radio'
                      aria-checked={rating === optionValue}
                      aria-label={ratingLabel(optionValue)}
                      aria-disabled={disabled}
                      tabIndex={disabled || optionValue !== focusableValue ? -1 : 0}
                      onClick={() => setRating(optionValue)}
                      onKeyDown={(event: React.KeyboardEvent<HTMLSpanElement>) => handleKeyDown(event, optionValue)}
                      onMouseEnter={() => !disabled && setHovered(optionValue)}
                    />
                  ))}
                </FractionalStar>
              );
            })}
          </StarRow>
        );
      }}
    />
  );
};

const StarRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const HalfTarget = styled.span<{ $side: 'left' | 'right' }>`
  position: absolute;
  top: 0;
  ${({ $side }) => ($side === 'left' ? 'left: 0;' : 'right: 0;')}
  width: 50%;
  height: 100%;
  cursor: pointer;

  &[aria-disabled='true'] {
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 2px;
  }
`;
